import axios, {AxiosInstance} from 'axios';
import { refreshTokens } from "./user-service";
import { getGlobalClearSessionCallback } from '../context/AuthContext';
import {jwtDecode} from "jwt-decode";

const axiosInstance: AxiosInstance = axios.create({
    baseURL: '/api',
    headers: {
        'Content-Type': 'application/json',
    }
});

const isTokenValid = (token: string): boolean => {
    try {
        const decoded = jwtDecode(token);
        if (decoded && decoded.exp && decoded.exp >= Date.now() / 1000) {
            return true;
        }
        return false;
    } catch (err: any) {
        console.error(err);
        return false;
    }
}

axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('accessToken');
    if (token && isTokenValid(token)) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

let isRefreshing = false;
let failedQueue: any[] = [];

axiosInstance.interceptors.response.use(
    response => response,
    async error => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            const clearSession = getGlobalClearSessionCallback();
            originalRequest._retry = true;
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token: string) => {
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            resolve(axiosInstance(originalRequest));
                        },
                        reject: (err: any) => {
                            reject(err);
                        },
                    });
                });
            }
            isRefreshing = true;

            try {
                const refreshToken = localStorage.getItem('refreshToken');
                const userUuid = localStorage.getItem('userUuid');

                if (refreshToken && userUuid) {
                    const response = await refreshTokens(refreshToken, {uuid: userUuid});
                    if (response.status === 200 && response.data) {
                        const {idToken, accessToken} = response.data;
                        localStorage.setItem('idToken', idToken ?? '');
                        localStorage.setItem('accessToken', accessToken ?? '');

                        isRefreshing = false;
                        processQueue(null, accessToken);

                        originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
                        return axiosInstance(originalRequest);
                    } else {
                        const error = new Error("Failed refresh request");
                        isRefreshing = false;
                        processQueue(error, null);
                        if(clearSession) {
                            clearSession();
                            window.location.href = '/login';
                        }
                        return Promise.reject(error);
                    }
                } else {
                    console.log('No tokens');
                    const error = new Error("No valid refresh token or user id");
                    isRefreshing = false;
                    processQueue(error, null);
                    if(clearSession) {
                        clearSession();
                        window.location.href = '/login';
                    }
                    return Promise.reject(error);
                }
            } catch (err) {
                isRefreshing = false;
                processQueue(err, null);
                if(clearSession) {
                    clearSession();
                    window.location.href = '/login';
                }
                return Promise.reject(err);
            }
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;